import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { listPayRates, addPayRate, deletePayRate, PAY_TYPES, RATE_SOURCE_LABEL } from '../../lib/payRates';
import { parseLocalDate } from '../../lib/dateUtils';
import { format } from 'date-fns';

const SUFFIX = { hourly: '/hr', weekly: '/wk', bi_weekly: '/2wk', monthly: '/mo', annually: '/yr', per_visit: '/visit' };
const money = (n, t) => `$${Number(n).toFixed(2)}${SUFFIX[t] || ''}`;
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

const inputStyle = { padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' };

// The rows of a person's pay-rate history, newest first, with the one in force
// today marked, and a dated change for a manager to enter. Payroll pays each
// visit at the rate in force on its date, so a change entered here with a past
// date reaches that week the next time it is pulled.
export default function PayRateHistory({ staff, createdBy, onChanged }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ payType: staff.pay_type || 'hourly', payRate: '', effectiveFrom: today(), note: '' });

  const load = useCallback(async () => {
    try { setRows(await listPayRates(supabase, staff.id)); setError(''); }
    catch (e) { setError(e.message); }
  }, [staff.id]);
  useEffect(() => { load(); }, [load]);

  const inForce = rows.find(r => r.effective_from <= today());

  const save = async () => {
    setSaving(true);
    try {
      await addPayRate(supabase, { staffId: staff.id, ...form, createdBy });
      setAdding(false);
      setForm(f => ({ ...f, payRate: '', note: '' }));
      await load();
      onChanged?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove the ${money(r.pay_rate, r.pay_type)} rate from ${format(parseLocalDate(r.effective_from), 'MMM d, yyyy')}?`)) return;
    try { await deletePayRate(supabase, r.id); await load(); onChanged?.(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ padding: '8px 0 12px' }}>
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', padding: '6px 0' }}>{error}</div>}
      {rows.length === 0 && !error && (
        <div style={{ fontSize: 12, color: 'var(--text3)', padding: '6px 0' }}>No rate on file. Payroll pays nothing for this person's visits until one is entered.</div>
      )}
      {rows.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12, color: 'var(--text3)', width: 96, flexShrink: 0 }}>from {format(parseLocalDate(r.effective_from), 'MMM d, yyyy')}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{money(r.pay_rate, r.pay_type)}</span>
          {r === inForce && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, background: 'var(--success-bg)', color: 'var(--success)' }}>IN FORCE</span>}
          {r.effective_from > today() && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, background: 'var(--warn-bg)', color: 'var(--warn)' }}>UPCOMING</span>}
          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto', textAlign: 'right' }} title={r.note || ''}>{RATE_SOURCE_LABEL[r.source] || r.source}</span>
          <button onClick={() => remove(r)} title="Remove this rate" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13 }}>×</button>
        </div>
      ))}
      {adding ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingTop: 10 }}>
          <input type="date" value={form.effectiveFrom} onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))} style={inputStyle} aria-label="Starts" />
          <input type="number" min="0" step="0.01" placeholder="Rate" value={form.payRate} onChange={e => setForm(f => ({ ...f, payRate: e.target.value }))} style={{ ...inputStyle, width: 90 }} aria-label="Rate" />
          <select value={form.payType} onChange={e => setForm(f => ({ ...f, payType: e.target.value }))} style={inputStyle} aria-label="Pay type">
            {PAY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Note (optional)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{ ...inputStyle, flex: 1, minWidth: 120 }} />
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      ) : (
        <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => setAdding(true)}>+ Rate change</button>
      )}
    </div>
  );
}
